import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    DatePicker,
    Tag,
    Space,
    Popconfirm,
    message,
    Row,
    Col,
    Badge,
    Tooltip,
    Typography,
    Empty,
    Spin,
    Radio,
    Drawer,
    List,
    Descriptions,
    Checkbox
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    BellOutlined,
    UserOutlined,
    MedicineBoxOutlined,
    HeartOutlined,
    CheckCircleOutlined,
    PauseCircleOutlined,
    ExceptionOutlined,
    InfoCircleFilled,
    EyeOutlined,
    MenuOutlined
} from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import { useLanguage } from '../../../context/LanguageContext';

const { Title, Text } = Typography;
const { Option } = Select;

const scheduleTypes = [
    { value: 1, label: 'schedule.types.medicine', icon: <MedicineBoxOutlined />, color: '#298E9F' },
    { value: 2, label: 'schedule.types.behavior', icon: <HeartOutlined />, color: '#FEBA17' },
    { value: 3, label: 'schedule.types.other', icon: <ExceptionOutlined />, color: '#52c41a' },
];

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const apiService = {
    addSchedule: async (scheduleData) => {
        try {
            const response = await drEasyApi.post('/api/Schedule/Add', scheduleData);
            return response.data;
        } catch (error) {
            console.error('Add schedule error:', error);
            message.error(error.response.data[0])
            throw new Error(error.response?.data?.message || 'Failed to add schedule');
        }
    },

    assignSchedule: async (assignData) => {
        try {
            const response = await drEasyApi.post('/api/Schedule/Assign', assignData);
            return response.data;
        } catch (error) {
            console.error('Assign schedule error:', error);
            throw new Error(error.response?.data?.message || 'Failed to assign schedule');
        }
    },

    editSchedule: async (scheduleData) => {
        try {
            const response = await drEasyApi.put('/api/Schedule/Edit', scheduleData);
            return response.data;
        } catch (error) {
            console.error('Edit schedule error:', error);
            throw new Error(error.response?.data?.message || 'Failed to update schedule');
        }
    },

    // Delete schedule
    deleteSchedule: async (scheduleId) => {
        try {
            const response = await drEasyApi.delete(`/api/Schedule/Delete?ScheduleItemId=${scheduleId}`);
            return response.data;
        } catch (error) {
            console.error('Delete schedule error:', error);
            throw new Error(error.response?.data?.message || 'Failed to delete schedule');
        }
    },

    // Get all schedules
    getSchedules: async () => {
        try {
            const response = await drEasyApi.get('/api/Schedule/GetMySchedules');
            return response.data;
        } catch (error) {
            console.error('Get schedules error:', error);
            console.warn('API not available, returning empty array');
            return [];
        }
    }
};

const transformScheduleData = (apiData) => {
    return apiData.map(schedule => ({
        ...schedule,
        // Parse the JSON string to array if it exists
        reminderTimes: schedule.reminderTimesJson ?
            JSON.parse(schedule.reminderTimesJson) : (schedule.reminderTimes || []),
        // Use displayDays if available, otherwise convert from reminderDays
        displayDays: schedule.displayDays || 
            (schedule.reminderDays ? getDaysFromDayOfWeekNumbers(schedule.reminderDays) : [])
    }));
};

// Convert DayOfWeek enum numbers back to day names
const getDaysFromDayOfWeekNumbers = (dayNumbers) => {
    const numberMapping = {
        0: 'Sun',  // Sunday
        1: 'Mon',  // Monday
        2: 'Tue',  // Tuesday
        3: 'Wed',  // Wednesday
        4: 'Thu',  // Thursday
        5: 'Fri',  // Friday
        6: 'Sat'   // Saturday
    };

    if (!Array.isArray(dayNumbers)) return [];
    return dayNumbers.map(num => numberMapping[num]).filter(day => day !== undefined);
};

const ScheduleManagement = () => {
    const { t } = useLanguage();
    const [schedules, setSchedules] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [durationType, setDurationType] = useState('endDate');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [checked, setChecked] = useState(false)

    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadInitialData = async () => {
        setPageLoading(true);
        try {
            const schedulesData = await apiService.getSchedules();
            // Transform the data to match component expectations
            const transformedData = transformScheduleData(schedulesData || []);
            setSchedules(transformedData);
        } catch (error) {
            console.error('Failed to load initial data:', error);
            message.error(t('schedule.messages.loadError'));
            setSchedules([]); // Set empty array on error
        } finally {
            setPageLoading(false);
        }
    };

    const refreshData = async () => {
        setLoading(true);
        try {
            const schedulesData = await apiService.getSchedules();

            // Transform the data to match component expectations
            const transformedData = transformScheduleData(schedulesData || []);
            setSchedules(transformedData);
        } catch (error) {
            console.error('Failed to refresh data:', error);
            message.error(t('schedule.messages.refreshError'));
        } finally {
            setLoading(false);
        }
    };

    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 1024;

    const getScheduleTypeInfo = (type) => {
        const typeInfo = scheduleTypes.find(t => t.value === type) || scheduleTypes[0];
        return {
            ...typeInfo,
            label: t(typeInfo.label)
        };
    };

    const getDayOfWeekNumbers = (daysArray) => {
        // Convert day names to DayOfWeek enum numbers
        const dayMapping = {
            'Sun': 0,  // Sunday
            'Mon': 1,  // Monday
            'Tue': 2,  // Tuesday
            'Wed': 3,  // Wednesday
            'Thu': 4,  // Thursday
            'Fri': 5,  // Friday
            'Sat': 6   // Saturday
        };

        return daysArray.map(day => dayMapping[day]).filter(num => num !== undefined);
    };

    const formatScheduleData = (values) => {
        // Handle "Every Day" checkbox
        let reminderDays = [];
        if (checked) {
            // If "Every Day" is checked, include all days (0-6)
            reminderDays = [0, 1, 2, 3, 4, 5, 6];
        } else if (values.repeatDays && values.repeatDays.length > 0) {
            // Convert selected days to DayOfWeek enum numbers
            reminderDays = getDayOfWeekNumbers(values.repeatDays);
        }

        const baseData = {
            command: "Add", // Required field for API
            name: values.name,
            type: values.type,
            isActive: values.isActive !== undefined ? values.isActive : true,
            reminderDays: reminderDays, 
            reminderTimes: values.reminderTimes || [],
            startDate: values.startDate ? new Date(values.startDate).toISOString() : new Date().toISOString(),
            endDate: values.endDate ? new Date(values.endDate).toISOString() : null
        };

        if (values.repeatDaysNum && values.startDate) {
            const startDate = new Date(values.startDate);
            const endDate = new Date(startDate.getTime() + (values.repeatDaysNum * 24 * 60 * 60 * 1000));
            baseData.endDate = endDate.toISOString();
        }

        return baseData;
    };

    const handleAdd = async (values) => {
        setLoading(true);
        try {
            const scheduleData = formatScheduleData(values);
            await apiService.addSchedule(scheduleData);

            message.success(t('schedule.messages.createSuccess'));
            setModalVisible(false);
            form.resetFields();
            setDurationType('endDate'); // Reset duration type

            // Refresh the schedules list
            await refreshData();
        } catch (error) {
            console.error('Failed to create schedule:', error);
            message.error(error.message || t('schedule.messages.createError'));
        } finally {
            setLoading(false);
        }
    };

    // Update the handleEdit function to include the correct command
    const handleEdit = async (values) => {
        setLoading(true);
        try {
            const scheduleData = {
                command: "Edit", // Required field for Edit API
                id: editingSchedule.id,
                ...formatScheduleData(values)
            };

            await apiService.editSchedule(scheduleData);

            message.success(t('schedule.messages.updateSuccess'));
            resetModal();

            // Refresh the schedules list
            await refreshData();
        } catch (error) {
            console.error('Failed to update schedule:', error);
            message.error(error.message || t('schedule.messages.updateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (scheduleId) => {
        try {
            await apiService.deleteSchedule(scheduleId);
            message.success(t('schedule.messages.deleteSuccess'));

            // Refresh the schedules list
            await refreshData();
        } catch (error) {
            console.error('Failed to delete schedule:', error);
            message.error(error.message || t('schedule.messages.deleteError'));
        }
    };

    const handleDurationTypeChange = (e) => {
        setDurationType(e.target.value);
        // Clear the other field when switching
        if (e.target.value === 'endDate') {
            form.setFieldsValue({ totalDays: undefined });
        } else {
            form.setFieldsValue({ endDate: undefined });
        }
    };

    const openEditModal = (schedule) => {
        setEditingSchedule(schedule);

        // Use displayDays directly from the schedule
        const days = schedule.displayDays || [];
        const isEveryDay = days.length === 7;
        setChecked(isEveryDay);

        // Determine duration type based on existing data
        const hasEndDate = schedule.endDate;
        setDurationType(hasEndDate ? 'endDate' : 'totalDays');

        form.setFieldsValue({
            name: schedule.name,
            type: schedule.type,
            isActive: schedule.isActive,
            repeatDays: isEveryDay ? [] : days, // Clear if every day is selected
            reminderTimes: schedule.reminderTimes || [],
            startDate: schedule.startDate ? schedule.startDate.split('T')[0] : null, // Format for date input
            endDate: schedule.endDate ? schedule.endDate.split('T')[0] : null, // Format for date input
        });
        setModalVisible(true);
    };

    const resetModal = () => {
        setModalVisible(false);
        setEditingSchedule(null);
        form.resetFields();
        setDurationType('endDate');
        setChecked(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('schedule.noEndDate');
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        });
    };

    const showScheduleDetails = (schedule) => {
        setSelectedSchedule(schedule);
        setDetailsDrawerVisible(true);
    };

    // Mobile Card View Component
    const MobileScheduleCard = ({ schedule }) => {
        const typeInfo = getScheduleTypeInfo(schedule.type);
        // Use displayDays directly
        const days = schedule.displayDays || [];

        return (
            <Card
                className="mb-4 shadow-sm"
                style={{ backgroundColor: 'white', border: '1px solid #EBF8FA' }}
                bodyStyle={{ padding: '16px' }}
            >
                <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2 flex-1">
                            <span style={{ color: typeInfo.color }}>{typeInfo.icon}</span>
                            <div className="flex-1 min-w-0">
                                <Text strong className="text-base block truncate" style={{ color: '#195761' }}>
                                    {schedule.name}
                                </Text>
                                <Tag color={typeInfo.color} size="small" className="mt-1">
                                    {typeInfo.label}
                                </Tag>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                            {schedule.isActive ? (
                                <Badge status="success" />
                            ) : (
                                <Badge status="default" />
                            )}
                            <Button
                                type="text"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => showScheduleDetails(schedule)}
                                style={{ color: '#298E9F' }}
                            />
                        </div>
                    </div>

                    {/* Schedule Info */}
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm">
                            <CalendarOutlined style={{ color: '#759AA0', fontSize: '12px' }} />
                            <Text className="text-xs text-gray-600">
                                {formatDate(schedule.startDate)} - {formatDate(schedule.endDate)}
                            </Text>
                        </div>

                        {/* Days */}
                        <div className="flex flex-wrap gap-1">
                            {days.slice(0, 4).map(day => (
                                <Tag key={day} size="small" style={{
                                    backgroundColor: '#EBF8FA',
                                    color: '#195761',
                                    border: '1px solid #298E9F',
                                    fontSize: '10px'
                                }}>
                                    {t(`schedule.days.${day.toLowerCase()}`)}
                                </Tag>
                            ))}
                            {days.length > 4 && (
                                <Tag size="small" style={{
                                    backgroundColor: '#EBF8FA',
                                    color: '#195761',
                                    border: '1px solid #298E9F',
                                    fontSize: '10px'
                                }}>
                                    +{days.length - 4}
                                </Tag>
                            )}
                        </div>

                        {/* Reminders */}
                        <div className="flex items-center space-x-2">
                            <ClockCircleOutlined style={{ color: '#759AA0', fontSize: '12px' }} />
                            <div className="flex flex-wrap gap-1">
                                {schedule.reminderTimes?.slice(0, 2).map((time, index) => (
                                    <Tag key={index} color="#FEBA17" size="small" style={{ fontSize: '10px' }}>
                                        {time}
                                    </Tag>
                                )) || <Text type="secondary" className="text-xs">{t('schedule.noReminders')}</Text>}
                                {schedule.reminderTimes?.length > 2 && (
                                    <Tag color="#FEBA17" size="small" style={{ fontSize: '10px' }}>
                                        +{schedule.reminderTimes.length - 2}
                                    </Tag>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(schedule)}
                            style={{ color: '#298E9F' }}
                        >
                            {t('schedule.actions.edit')}
                        </Button>
                        <Popconfirm
                            title={t('schedule.confirmDelete.title')}
                            description={t('schedule.confirmDelete.description')}
                            onConfirm={() => handleDelete(schedule.id)}
                            okText={t('schedule.confirmDelete.yes')}
                            cancelText={t('schedule.confirmDelete.no')}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                            >
                                {t('schedule.actions.delete')}
                            </Button>
                        </Popconfirm>
                    </div>
                </div>
            </Card>
        );
    };

    // Desktop Table Columns
    const getColumns = () => {
        if (isMobile) return [];

        const baseColumns = [
            {
                title: t('schedule.table.scheduleDetails'),
                key: 'details',
                width: isTablet ? 200 : 280,
                render: (_, record) => {
                    const typeInfo = getScheduleTypeInfo(record.type);
                    return (
                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <span style={{ color: typeInfo.color }}>{typeInfo.icon}</span>
                                <div className="flex-1 min-w-0">
                                    <Text strong className={isTablet ? "text-sm" : "text-base"} style={{ color: '#195761' }}>
                                        {record.name}
                                    </Text>
                                    {record.isActive ? (
                                        <Badge status="success" text={t('schedule.status.active')} className="ml-2" />
                                    ) : (
                                        <Badge status="default" text={t('schedule.status.inactive')} className="ml-2" />
                                    )}
                                </div>
                            </div>
                            <div>
                                <Tag color={typeInfo.color} size={isTablet ? "small" : "default"}>
                                    {typeInfo.label}
                                </Tag>
                            </div>
                        </div>
                    );
                }
            }
        ];

        if (!isTablet) {
            baseColumns.push({
                title: t('schedule.table.schedule'),
                key: 'schedule',
                width: 220,
                render: (_, record) => {
                    // Use displayDays directly
                    const days = record.displayDays || [];
                    return (
                        <div className="space-y-2">
                            <div className="flex items-center space-x-1">
                                <CalendarOutlined style={{ color: '#759AA0' }} />
                                <Text className="text-sm">
                                    {formatDate(record.startDate)} - {formatDate(record.endDate)}
                                </Text>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {days.map(day => (
                                    <Tag key={day} size="small" style={{
                                        backgroundColor: '#EBF8FA',
                                        color: '#195761',
                                        border: '1px solid #298E9F'
                                    }}>
                                        {t(`schedule.days.${day.toLowerCase()}`)}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    );
                }
            });

            baseColumns.push({
                title: t('schedule.table.reminders'),
                key: 'reminders',
                width: 200,
                render: (_, record) => (
                    <div className="space-y-1">
                        <div className="flex items-center space-x-1">
                            <ClockCircleOutlined style={{ color: '#759AA0' }} />
                            <Text className="text-sm font-medium">{t('schedule.times')}</Text>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {record.reminderTimes && record.reminderTimes.length > 0 ? (
                                record.reminderTimes.map((time, index) => (
                                    <Tag key={index} icon={<BellOutlined />} color="#FEBA17" size="small">
                                        {time}
                                    </Tag>
                                ))
                            ) : (
                                <Text type="secondary" className="text-sm">{t('schedule.noReminders')}</Text>
                            )}
                        </div>
                    </div>
                )
            });
        }

        baseColumns.push({
            title: t('schedule.table.actions'),
            key: 'actions',
            width: isTablet ? 100 : 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    {isTablet && (
                        <Tooltip title={t('schedule.actions.viewDetails')}>
                            <Button
                                type="text"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => showScheduleDetails(record)}
                                style={{ color: '#298E9F' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t('schedule.actions.edit')}>
                        <Button
                            type="text"
                            size={isTablet ? "small" : "middle"}
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                            style={{ color: '#298E9F' }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('schedule.confirmDelete.title')}
                        description={t('schedule.confirmDelete.description')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('schedule.confirmDelete.yes')}
                        cancelText={t('schedule.confirmDelete.no')}
                    >
                        <Tooltip title={t('schedule.actions.delete')}>
                            <Button
                                type="text"
                                size={isTablet ? "small" : "middle"}
                                icon={<DeleteOutlined />}
                                danger
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        });

        return baseColumns;
    };

    if (pageLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7FCFD' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F7FCFD', padding: isMobile ? '16px' : '24px' }}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 min-w-0 mr-4">
                            <Title level={isMobile ? 3 : 2} className="mb-2" style={{ color: '#195761' }}>
                                <BellOutlined className="mr-2" />
                                {isMobile ? t('schedule.title.short') : t('schedule.title.full')}
                            </Title>
                            <Text className={isMobile ? "text-sm" : "text-lg"} style={{ color: '#759AA0' }}>
                                {isMobile ? t('schedule.subtitle.short') : t('schedule.subtitle.full')}
                            </Text>
                        </div>
                        <Button
                            type="primary"
                            size={isMobile ? "middle" : "large"}
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setModalVisible(true);
                                setDurationType('endDate');
                            }}
                            style={{ backgroundColor: '#298E9F', borderColor: '#298E9F' }}
                        >
                            {isMobile ? t('schedule.actions.add') : t('schedule.actions.addSchedule')}
                        </Button>
                    </div>

                    {/* Stats Cards */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={24} sm={8}>
                            <Card className="text-center" style={{ backgroundColor: '#EBF8FA', border: '1px solid #298E9F' }}>
                                <BellOutlined style={{ fontSize: '24px', color: '#298E9F' }} />
                                <div className="mt-2">
                                    <Text strong className="text-xl block" style={{ color: '#195761' }}>{schedules.length}</Text>
                                    <Text style={{ color: '#759AA0', fontSize: isMobile ? '12px' : '14px' }}>
                                        {t('schedule.stats.totalSchedules')}
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="text-center" style={{ backgroundColor: '#F6FFED', border: '1px solid #52c41a' }}>
                                <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                                <div className="mt-2">
                                    <Text strong className="text-xl block" style={{ color: '#195761' }}>
                                        {schedules.filter(s => s.isActive).length}
                                    </Text>
                                    <Text style={{ color: '#759AA0', fontSize: isMobile ? '12px' : '14px' }}>
                                        {t('schedule.stats.activeSchedules')}
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card className="text-center" style={{ backgroundColor: '#FEF7E6', border: '1px solid #FEBA17' }}>
                                <UserOutlined style={{ fontSize: '24px', color: '#FEBA17' }} />
                                <div className="mt-2">
                                    <Text strong className="text-xl block" style={{ color: '#195761' }}>
                                        {schedules.filter(s => s.type === 0).length}
                                    </Text>
                                    <Text style={{ color: '#759AA0', fontSize: isMobile ? '12px' : '14px' }}>
                                        {t('schedule.stats.medicationSchedules')}
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* Schedules Content */}
                {isMobile ? (
                    // Mobile Card View
                    <div>
                        {schedules.length > 0 ? (
                            schedules.map(schedule => (
                                <MobileScheduleCard key={schedule.id} schedule={schedule} />
                            ))
                        ) : (
                            <Card className="text-center py-8">
                                <Empty
                                    description={t('schedule.empty.description')}
                                    image={<BellOutlined style={{ fontSize: 48, color: '#759AA0' }} />}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => setModalVisible(true)}
                                        style={{ backgroundColor: '#298E9F', borderColor: '#298E9F' }}
                                    >
                                        {t('schedule.empty.addFirstSchedule')}
                                    </Button>
                                </Empty>
                            </Card>
                        )}
                    </div>
                ) : (
                    // Desktop/Tablet Table View
                    <Card
                        className="shadow-sm"
                        style={{ backgroundColor: 'white', border: '1px solid #EBF8FA' }}
                    >
                        <Table
                            columns={getColumns()}
                            dataSource={schedules}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: !isTablet,
                                showQuickJumper: !isTablet,
                                showTotal: (total, range) =>
                                    isTablet ? `${range[0]}-${range[1]} of ${total}` :
                                        t('schedule.pagination.total', { start: range[0], end: range[1], total }),
                                size: isTablet ? 'small' : 'default'
                            }}
                            size={isTablet ? 'small' : 'middle'}
                            locale={{
                                emptyText: (
                                    <Empty
                                        description={t('schedule.empty.description')}
                                        image={<BellOutlined style={{ fontSize: 48, color: '#759AA0' }} />}
                                    >
                                        <Button
                                            type="primary"
                                            onClick={() => setModalVisible(true)}
                                            style={{ backgroundColor: '#298E9F', borderColor: '#298E9F' }}
                                        >
                                            {t('schedule.empty.addFirstSchedule')}
                                        </Button>
                                    </Empty>
                                )
                            }}
                        />
                    </Card>
                )}

                {/* Details Drawer for Mobile/Tablet */}
                <Drawer
                    title={
                        <Space>
                            <BellOutlined style={{ color: '#298E9F' }} />
                            {t('schedule.drawer.title')}
                        </Space>
                    }
                    placement="right"
                    onClose={() => setDetailsDrawerVisible(false)}
                    open={detailsDrawerVisible}
                    width={isMobile ? '100%' : 400}
                >
                    {selectedSchedule && (
                        <div className="space-y-4">
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label={t('schedule.form.name')}>
                                    <Text strong>{selectedSchedule.name}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('schedule.form.type')}>
                                    <Tag color={getScheduleTypeInfo(selectedSchedule.type).color}>
                                        {getScheduleTypeInfo(selectedSchedule.type).label}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('schedule.form.status')}>
                                    {selectedSchedule.isActive ? (
                                        <Badge status="success" text={t('schedule.status.active')} />
                                    ) : (
                                        <Badge status="default" text={t('schedule.status.inactive')} />
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('schedule.drawer.duration')}>
                                    {formatDate(selectedSchedule.startDate)} - {formatDate(selectedSchedule.endDate)}
                                </Descriptions.Item>
                            </Descriptions>

                            <div>
                                <Text strong className="block mb-2">{t('schedule.form.repeatDays')}</Text>
                                <div className="flex flex-wrap gap-1">
                                    {(selectedSchedule.displayDays || []).map(day => (
                                        <Tag key={day} style={{
                                            backgroundColor: '#EBF8FA',
                                            color: '#195761',
                                            border: '1px solid #298E9F'
                                        }}>
                                            {t(`schedule.days.${day.toLowerCase()}`)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Text strong className="block mb-2">{t('schedule.drawer.reminderTimes')}</Text>
                                <div className="flex flex-wrap gap-1">
                                    {selectedSchedule.reminderTimes?.length > 0 ? (
                                        selectedSchedule.reminderTimes.map((time, index) => (
                                            <Tag key={index} icon={<BellOutlined />} color="#FEBA17">
                                                {time}
                                            </Tag>
                                        ))
                                    ) : (
                                        <Text type="secondary">{t('schedule.drawer.noReminders')}</Text>
                                    )}
                                </div>
                            </div>

                            <div className="pt-4 border-t">
                                <Space className="w-full justify-center">
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            setDetailsDrawerVisible(false);
                                            openEditModal(selectedSchedule);
                                        }}
                                        style={{ backgroundColor: '#298E9F', borderColor: '#298E9F' }}
                                    >
                                        {t('schedule.actions.editSchedule')}
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    )}
                </Drawer>

                {/* Add/Edit Modal */}
                <Modal
                    title={
                        <Space>
                            <BellOutlined style={{ color: '#298E9F' }} />
                            {editingSchedule ? t('schedule.modal.editTitle') : t('schedule.modal.addTitle')}
                        </Space>
                    }
                    open={modalVisible}
                    onCancel={resetModal}
                    footer={[
                        <Button key="cancel" onClick={resetModal}>
                            {t('schedule.actions.cancel')}
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={loading}
                            onClick={() => form.submit()}
                            style={{ backgroundColor: '#298E9F', borderColor: '#298E9F' }}
                        >
                            {editingSchedule ? t('schedule.actions.updateSchedule') : t('schedule.actions.createSchedule')}
                        </Button>
                    ]}
                    width={isMobile ? '95%' : 800}
                    style={isMobile ? { top: 20 } : {}}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={editingSchedule ? handleEdit : handleAdd}
                        initialValues={{ isActive: true }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="name"
                                    label={t('schedule.form.name')}
                                    rules={[{ required: true, message: t('schedule.form.nameRequired') }]}
                                >
                                    <Input placeholder={t('schedule.form.namePlaceholder')} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="type"
                                    label={t('schedule.form.type')}
                                    rules={[{ required: true, message: t('schedule.form.typeRequired') }]}
                                >
                                    <Select placeholder={t('schedule.form.typePlaceholder')}>
                                        {scheduleTypes.map(type => (
                                            <Option key={type.value} value={type.value}>
                                                <Space>
                                                    {type.icon}
                                                    {t(type.label)}
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="startDate" label={t('schedule.form.startDate')}>
                                    <Input type="date" style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="durationType" label={t('schedule.form.duration')}>
                            <Radio.Group onChange={handleDurationTypeChange} value={durationType}>
                                <Radio.Button value="endDate">
                                    <CalendarOutlined /> {t('schedule.form.setEndDate')}
                                </Radio.Button>
                                <Radio.Button value="totalDays">
                                    <InfoCircleFilled /> {t('schedule.form.setTotalDays')}
                                </Radio.Button>
                            </Radio.Group>
                        </Form.Item>

                        {/* Conditional fields based on duration type */}
                        {durationType === 'endDate' ? (
                            <Form.Item name="endDate" label={t('schedule.form.endDate')}>
                                <Input type="date" style={{ width: '100%' }} />
                            </Form.Item>
                        ) : (
                            <Form.Item name="repeatDaysNum" label={t('schedule.form.repeatedNumber')}>
                                <Input type='number' />
                            </Form.Item>
                        )}

                        <Row gutter={16} className='flex items-center justify-center'>
                            <Col xs={24} sm={12}>
                                <Form.Item name="repeatDays" label={t('schedule.form.repeatDays')}>
                                    <Select mode="multiple" placeholder={t('schedule.form.selectDays')} disabled={checked}>
                                        {dayNames.map(day => (
                                            <Option key={day} value={day}>{t(`schedule.days.${day.toLowerCase()}`)}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} className='mt-7'>
                                <Form.Item name="everDay" valuePropName="checked">
                                    <Checkbox
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setChecked(isChecked);

                                            // If "Every Day" is checked, clear the repeatDays selection
                                            if (isChecked) {
                                                form.setFieldsValue({ repeatDays: [] });
                                            }
                                        }}
                                        checked={checked}
                                    >
                                        {t('schedule.form.everyDay')}
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="reminderTimes" label={t('schedule.form.reminderTimes')}>
                            <Select
                                mode="tags"
                                placeholder={t('schedule.form.reminderTimesPlaceholder')}
                                style={{ width: '100%' }}
                            >
                                {Array.from({ length: 24 }, (_, i) => {
                                    const times = [];
                                    times.push(`${i.toString().padStart(2, '0')}:00`);
                                    times.push(`${i.toString().padStart(2, '0')}:30`);
                                    return times;
                                }).flat().map(time => (
                                    <Option key={time} value={time}>
                                        {time}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="isActive" valuePropName="checked" label={t('schedule.form.activeStatus')}>
                            <Switch
                                checkedChildren={<CheckCircleOutlined />}
                                unCheckedChildren={<PauseCircleOutlined />}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default ScheduleManagement;